<template>
  <div class="container">
    <!-- Alerts -->
    <div class="row">
      <div class="col-md-6">
        <div class="box mt-2">
          <router-link :to="{ name: 'admin.user-registration-requests.index' }" class="text-decoration-none text-reset">
            <div class="alert-card">
              <div class="alert-label">{{ $t('adminDashboard.pendingRequests') }}</div>
              <div class="alert-value" :class="dashboard && dashboard.pendingUserRegistrationRequests > 0 ? 'text-warning-emphasis' : 'text-success'">
                {{ dashboard ? dashboard.pendingUserRegistrationRequests.toLocaleString() : '-' }}
                <span class="alert-unit">{{ $t('adminDashboard.countUnit') }}</span>
              </div>
            </div>
          </router-link>
        </div>
      </div>
      <div class="col-md-6">
        <div class="box mt-2">
          <router-link :to="{ name: 'admin.api-call-histories.index' }" class="text-decoration-none text-reset">
            <div class="alert-card">
              <div class="alert-label">{{ $t('adminDashboard.errors24h') }}</div>
              <div class="alert-value" :class="totalErrors > 0 ? 'text-danger' : 'text-success'">
                {{ dashboard ? totalErrors.toLocaleString() : '-' }}
                <span class="alert-unit">{{ $t('adminDashboard.countUnit') }}</span>
              </div>
              <div v-if="dashboard" class="alert-breakdown">
                {{ $t('adminDashboard.errorsBreakdown', { api: dashboard.apiErrors24h.toLocaleString(), operation: dashboard.operationErrors24h.toLocaleString() }) }}
              </div>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <!-- Data provider activity -->
    <div class="row">
      <div class="col-12">
        <div class="box mt-2">
          <div class="heading">
            <h3>{{ $t('adminDashboard.providersTitle') }}</h3>
          </div>
          <table v-if="dashboard && dashboard.providers.length > 0" class="table table-sm table-list table-hover mt-2">
            <thead>
            <tr>
              <th>{{ $t('adminDashboard.providerName') }}</th>
              <th>{{ $t('adminDashboard.lastCalledAt') }}</th>
              <th class="text-center">{{ $t('adminDashboard.calls24h') }}</th>
              <th class="text-center">{{ $t('adminDashboard.providerErrors24h') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="provider in dashboard.providers" :key="provider.id" :class="{ 'text-muted': !provider.lastCalledAt }" @click="moveToProvider(provider.id)" class="pointer">
              <td>{{ provider.name }}</td>
              <td>{{ provider.lastCalledAt ? formatDateTime(provider.lastCalledAt) : $t('adminDashboard.neverCalled') }}</td>
              <td class="text-center">{{ provider.calls24h.toLocaleString() }}</td>
              <td class="text-center" :class="{ 'text-danger fw-bold': provider.errors24h > 0 }">{{ provider.errors24h.toLocaleString() }}</td>
            </tr>
            </tbody>
          </table>
          <div v-else class="text-center mt-4 mb-3">
            <div v-if="isLoading">{{ $t('adminDashboard.loading') }}</div>
            <div v-else>{{ $t('adminDashboard.noProviders') }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Data volume snapshot -->
    <div class="row">
      <div class="col-12">
        <div class="box mt-2">
          <div class="heading">
            <h3>{{ $t('adminDashboard.tablesTitle') }}</h3>
          </div>
          <table v-if="dashboard && dashboard.tables.length > 0" class="table table-sm table-list table-hover mt-2">
            <thead>
            <tr>
              <th>{{ $t('masterData.tableNameHeader') }}</th>
              <th class="text-center">{{ $t('masterData.rowCountHeader') }}</th>
              <th>{{ $t('masterData.lastUpdatedHeader') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="table in dashboard.tables" :key="table.name">
              <td>
                <router-link :to="{ name: 'admin.master-data.show', params: { tableName: table.name } }" class="text-decoration-none">
                  {{ table.name }}
                </router-link>
              </td>
              <td class="text-center">{{ table.rowCount.toLocaleString() }}</td>
              <td>{{ table.lastUpdatedAt ? formatDateTime(table.lastUpdatedAt) : '-' }}</td>
            </tr>
            </tbody>
          </table>
          <div v-else class="text-center mt-4 mb-3">
            <div v-if="isLoading">{{ $t('adminDashboard.loading') }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue';
import {useRouter} from 'vue-router';
import axios from 'axios';
import {formatDateTime} from '../../utils/DateTimeUtil';

const router = useRouter();
const dashboard = ref(null);
const isLoading = ref(true);

const totalErrors = computed(() => {
  if (!dashboard.value) return 0;
  return dashboard.value.apiErrors24h + dashboard.value.operationErrors24h;
});

/**
 * ダッシュボード集計を取得
 */
const fetchDashboard = async () => {
  try {
    isLoading.value = true;
    const response = await axios.get('/api/admin/dashboard');
    dashboard.value = response.data;
  } catch (error) {
    console.error('Error fetching admin dashboard:', error);
  } finally {
    isLoading.value = false;
  }
};

const moveToProvider = (id) => {
  router.push({ name: 'admin.data-providers.show', params: { id } });
};

onMounted(() => {
  fetchDashboard();
});
</script>

<style lang="scss" scoped>
.alert-card {
  padding: 8px 4px;

  .alert-label {
    font-size: 14px;
    color: #6c757d;
  }

  .alert-value {
    font-size: 32px;
    font-weight: 600;
    line-height: 1.2;

    .alert-unit {
      font-size: 14px;
      font-weight: 400;
    }
  }

  .alert-breakdown {
    font-size: 12px;
    color: #6c757d;
  }
}

.pointer {
  cursor: pointer;
}
</style>
